import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, BookOpen, Smartphone, HeartOff } from 'lucide-react';
import { useInView } from '../hooks/useInView';
const items = [{
  icon: Sparkles,
  text: 'Buscas resultados instantáneos sin dedicar tiempo a la introspección.'
}, {
  icon: BookOpen,
  text: 'No disfrutas espacios de lectura, calma o reflexión personal.'
}, {
  icon: Smartphone,
  text: 'Prefieres consumir contenido rápido en lugar de crear rituales conscientes.'
}, {
  icon: HeartOff,
  text: 'Esperas soluciones externas sin involucrarte emocionalmente en el proceso.'
}];
export function NoEsParaTiSection() {
  const [headerRef, headerInView] = useInView({
    threshold: 0.2
  });
  const [gridRef, gridInView] = useInView({
    threshold: 0.1
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section id="no-es-para-ti" className="relative py-16 sm:py-24 bg-[#0A0A0B] border-t border-white/5 overflow-hidden scroll-mt-[56px] sm:scroll-mt-[70px]" aria-label="No es para ti si" data-id="element-396">
      {/* Subtle gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.06),transparent_70%)] pointer-events-none" data-id="element-397" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-398">
        {/* Header */}
        <motion.div ref={headerRef} initial={{
        opacity: 0,
        y: 30
      }} animate={headerInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12 sm:mb-16" data-id="element-399">
          <span className="inline-block text-gold text-[10px] sm:text-xs font-bold tracking-[3px] uppercase mb-4" data-id="element-400">
            Lectura honesta
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-black text-white mb-5 leading-tight" data-id="element-401">
            No es para ti <span className="text-gold" data-id="element-402">si…</span>
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed" data-id="element-403">
            Esta experiencia fue creada para personas que desean crear momentos
            reales de pausa, claridad y conexión personal.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div ref={gridRef} variants={containerVariants} initial="hidden" animate={gridInView ? 'visible' : 'hidden'} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16" data-id="element-404">
          {items.map((item, idx) => {
          const Icon = item.icon;
          return <motion.div key={idx} variants={itemVariants} className="group relative flex items-start gap-4 bg-[#0c0c0e] border border-gold/15 rounded-2xl p-5 sm:p-7 transition-all duration-300 hover:border-gold/35 hover:shadow-[0_0_30px_rgba(212,175,55,0.08)]" data-id="element-405">
                {/* Icon */}
                <div className="shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-gold/30 bg-gold/5 text-gold transition-colors duration-300 group-hover:bg-gold/10" data-id="element-406">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" data-id="element-407" />
                </div>

                {/* Text */}
                <p className="text-white/85 text-sm sm:text-base leading-relaxed font-medium pt-1.5 sm:pt-2.5" data-id="element-408">
                  {item.text}
                </p>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing line */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={gridInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="relative max-w-3xl mx-auto" data-id="element-409">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.08),transparent_70%)] blur-2xl pointer-events-none" data-id="element-410" />
          <div className="relative border-t border-gold/20 pt-8 sm:pt-10" data-id="element-411">
            <p className="text-center text-white/85 text-base sm:text-lg md:text-xl italic font-medium leading-relaxed" data-id="element-412">
              El Código 888 fue diseñado como una experiencia privada de{' '}
              <span className="text-gold not-italic font-bold" data-id="element-413">
                bienestar emocional
              </span>
              , <span className="text-gold not-italic font-bold" data-id="element-414">claridad</span>{' '}
              y{' '}
              <span className="text-gold not-italic font-bold" data-id="element-415">
                presencia interior
              </span>
              .
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}